// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

const WORD_MASK = 0xffffffff

function hammingWeight (v) {
  v -= (v >>> 1) & 0x55555555 // works with signed or unsigned shifts
  v = (v & 0x33333333) + ((v >>> 2) & 0x33333333)
  return (((v + (v >>> 4)) & 0xf0f0f0f) * 0x1010101) >>> 24
}

export class BitSet {
  public static BITS_PER_WORD = Uint32Array.BYTES_PER_ELEMENT * 8

  private words: Uint32Array

  private wordsCount: number = 0

  constructor (length = BitSet.BITS_PER_WORD) {
    this.words = new Uint32Array(this.wordIndex(length - 1) + 1)
  }

  /**
   * @returns the size of this: the index of the highest set bit in the 1;
   */
  public get size (): number {
    if (this.wordsCount === 0) {
      return 0
    }
    const wordsCount = this.wordsCount
    return BitSet.BITS_PER_WORD * (wordsCount - 1) + (BitSet.BITS_PER_WORD - Math.clz32(this.words[wordsCount - 1]))
  }

  public get bitLength (): number {
    return this.words.length * BitSet.BITS_PER_WORD
  }

  public set (bitIndex: number, value = true): void {
    if (value === false) {
      this.clear(bitIndex)
      return
    }

    if (bitIndex < 0) {
      throw new Error('bitIndex < 0: ' + bitIndex)
    }
    const wordIndex = this.wordIndex(bitIndex)
    this.expandTo(wordIndex)
    this.words[wordIndex] |= 1 << bitIndex
  }

  /**
   *
   * @param fromIndex (inclusive)
   * @param toIndex (exclusive)
   * @param value
   */
  public setRange (fromIndex: number, toIndex: number, value = true): void {
    if (value === false) {
      this.clearRange(fromIndex, toIndex)
      return
    }

    if (fromIndex < 0) {
      throw new Error('fromIndex < 0: ' + fromIndex)
    }
    if (fromIndex > toIndex) {
      throw new Error(`fromIndex ${fromIndex} > toIndex ${toIndex}`)
    }
    if (fromIndex === toIndex) {
      return
    }

    const startWordIndex = this.wordIndex(fromIndex)
    const endWordIndex = this.wordIndex(toIndex - 1)
    this.expandTo(endWordIndex)

    const firstWordMask = WORD_MASK << fromIndex
    const lastWordMask = WORD_MASK >>> -toIndex
    if (startWordIndex === endWordIndex) {
      this.words[startWordIndex] |= firstWordMask & lastWordMask
    } else {
      this.words[startWordIndex] |= firstWordMask
      for (let i = startWordIndex + 1; i < endWordIndex; i++) {
        this.words[i] = WORD_MASK
      }
      this.words[endWordIndex] |= lastWordMask
    }
  }

  public clear (bitIndex?: number): void {
    if (arguments.length === 0) {
      for (let i = 0; i < this.words.length; i++) {
        this.words[i] = 0
      }
      return
    }

    if (bitIndex < 0) {
      throw new Error('bitIndex < 0: ' + bitIndex)
    }
    const wordIndex = this.wordIndex(bitIndex)
    if (wordIndex <= this.words.length) {
      this.words[wordIndex] &= ~(1 << bitIndex)
    }
    this.calculateWordsCount()
  }

  public clearRange (fromIndex: number, toIndex: number): void {
    if (fromIndex < 0) {
      throw new Error('fromIndex < 0: ' + fromIndex)
    }
    if (fromIndex > toIndex) {
      throw new Error(`fromIndex ${fromIndex} > toIndex ${toIndex}`)
    }
    if (fromIndex === toIndex) {
      return
    }

    const wordsLength = this.words.length
    const startWordIndex = this.wordIndex(fromIndex)
    if (startWordIndex >= wordsLength) {
      return
    }

    let endWordIndex = this.wordIndex(toIndex - 1)
    if (endWordIndex >= wordsLength) {
      toIndex = this.size
      endWordIndex = wordsLength - 1
    }

    const firstWordMask = WORD_MASK << fromIndex
    const lastWordMask = WORD_MASK >>> -toIndex
    if (startWordIndex === endWordIndex) {
      this.words[startWordIndex] &= ~(firstWordMask & lastWordMask)
    } else {
      this.words[startWordIndex] &= ~firstWordMask
      for (let i = startWordIndex + 1; i < endWordIndex; i++) {
        this.words[i] = 0
      }
      this.words[endWordIndex] &= ~lastWordMask
    }
    this.calculateWordsCount()
  }

  public flip (bitIndex: number): void {
    if (bitIndex < 0) {
      throw new Error('bitIndex < 0: ' + bitIndex)
    }
    const wordIndex = this.wordIndex(bitIndex)
    this.expandTo(wordIndex)
    this.words[wordIndex] ^= 1 << bitIndex
    this.calculateWordsCount()
  }

  public flipRange (fromIndex: number, toIndex: number): void {
    if (fromIndex < 0) {
      throw new Error('fromIndex < 0: ' + fromIndex)
    }
    if (fromIndex > toIndex) {
      throw new Error(`fromIndex ${fromIndex} > toIndex ${toIndex}`)
    }
    if (fromIndex === toIndex) {
      return
    }

    const startWordIndex = this.wordIndex(fromIndex)
    const endWordIndex = this.wordIndex(toIndex - 1)
    this.expandTo(endWordIndex)

    const firstWordMask = WORD_MASK << fromIndex
    const lastWordMask = WORD_MASK >>> -toIndex
    if (startWordIndex === endWordIndex) {
      this.words[startWordIndex] ^= firstWordMask & lastWordMask
    } else {
      this.words[startWordIndex] ^= firstWordMask
      for (let i = startWordIndex + 1; i < endWordIndex; i++) {
        this.words[i] ^= WORD_MASK
      }
      this.words[endWordIndex] ^= lastWordMask
    }
    this.calculateWordsCount()
  }

  public get (bitIndex: number): boolean {
    if (bitIndex < 0) {
      throw new Error('bitIndex < 0: ' + bitIndex)
    }
    const wordIndex = this.wordIndex(bitIndex)
    return wordIndex < this.words.length && !!(this.words[wordIndex] & (1 << bitIndex))
  }

  /**
   * return the number of bits set to true;
   */
  public count (): number {
    let sum = 0
    for (let i = 0; i < this.wordsCount; i++) {
      sum += hammingWeight(this.words[i])
    }
    return sum
  }

  public toString (): string {
    let strArr = []
    for (let i = 0; i < this.words.length; i++) {
      strArr.push((this.words[i] >>> 0).toString(2))
    }
    return strArr.join(',')
  }

  public equals (obj: any): boolean {
    if (!(obj instanceof BitSet)) {
      return false
    }
    if (this === obj) {
      return true
    }

    const set = obj as BitSet
    if (this.wordsCount !== set.wordsCount) {
      return false
    }

    for (let i = 0; i < this.wordsCount; i++) {
      if (this.words[i] !== set.words[i]) {
        return false
      }
    }

    return true
  }

  public clone (): BitSet {
    const clone = Object.create(BitSet.prototype)
    clone.wordsCount = this.wordsCount
    clone.words = new Uint32Array(this.words)
    return clone
  }

  public intersects (set: BitSet): boolean {
    for (let i = Math.min(this.wordsCount, set.wordsCount) - 1; i >= 0; i--) {
      if ((this.words[i] & set.words[i]) !== 0) {
        return true
      }
    }
    return false
  }

  public and (set: BitSet): void {
    if (this === set) {
      return
    }

    while (this.wordsCount > set.wordsCount) {
      this.words[--this.wordsCount] = 0
    }

    for (let i = 0; i < this.wordsCount; i++) {
      this.words[i] &= set.words[i]
    }

    this.calculateWordsCount()
  }

  public or (set: BitSet): void {}
  public xor (set: BitSet): void {}
  public not (set: BitSet): void {}

  private wordIndex (bitIndex: number): number {
    return bitIndex >> 5
  }

  private expandTo (wordIndex: number): void {
    const wordsRequired = wordIndex + 1
    if (this.wordsCount < wordsRequired) {
      this.ensureCapacity(wordsRequired)
      this.wordsCount = wordsRequired
    }
  }

  private ensureCapacity (wordsRequired: number): void {
    if (this.words.length < wordsRequired) {
      //copy array
      const newWords = new Uint32Array(Math.max(2 * this.words.length, wordsRequired))
      newWords.set(this.words)
      this.words = newWords
    }
  }

  private calculateWordsCount (): void {
    let i
    for (i = this.words.length - 1; i >= 0; i--) {
      if (this.words[i] !== 0) {
        break
      }
    }
    this.wordsCount = i + 1
  }
}
